import React, { useState, useEffect, useRef } from 'react';
import { Package, Truck, ClipboardCheck, UserCheck, BellRing, CheckCircle2, ShieldCheck, User, Users, Check, AlertCircle, CalendarRange, Send, RotateCcw, MessageCircle, ChevronLeft, ChevronRight, MapPin, ExternalLink } from 'lucide-react';
import PageTransition from '../../components/PageTransition';
import api from '../../api/axios';

export default function PuntoEntregaPage() {
  const [openedCards, setOpenedCards] = useState({ 1: false, 2: false, 3: false });
  const [drawPath, setDrawPath] = useState(false);
  const pathSectionRef = useRef(null);
  const [activePlanIndex, setActivePlanIndex] = useState(0);

  const scrollContainerRef = useRef(null);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const scrollPosition = container.scrollLeft;
    const containerWidth = container.clientWidth;
    const cardWidth = container.querySelector('.snap-center')?.clientWidth || (containerWidth * 0.80);
    const gap = 24; // gap-6
    const index = Math.round(scrollPosition / (cardWidth + gap));
    if (index >= 0 && index < plansData.length) {
      setActivePlanIndex(index);
    }
  };

  const scrollToPlan = (idx) => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const cardWidth = container.querySelector('.snap-center')?.clientWidth || (container.clientWidth * 0.80);
    const gap = 24; // gap-6
    container.scrollTo({
      left: idx * (cardWidth + gap),
      behavior: 'smooth'
    });
    setActivePlanIndex(idx);
  };

  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');

  const toggleCard = (id) => {
    setOpenedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setDrawPath(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (pathSectionRef.current) {
      observer.observe(pathSectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  // Obtener sucursales registradas en la base de datos
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await api.get('/branches');
        const raw = res.data?.data || res.data;
        const allBranches = Array.isArray(raw) ? raw : [];
        // Filtrar solo las sucursales que cuenten con un teléfono registrado y coordenadas lat/lng
        const validBranches = allBranches.filter(b => b && b.phone && b.phone.trim() !== '');
        setBranches(validBranches);
        if (validBranches.length > 0) {
          setSelectedBranchId(validBranches[0].id);
        }
      } catch (err) {
        console.error("Error fetching branches for WhatsApp button:", err);
      }
    };
    fetchBranches();
  }, []);

  const safeBranches = Array.isArray(branches) ? branches : [];
  const selectedBranch = safeBranches.find(b => b && b.id === parseInt(selectedBranchId)) || safeBranches[0];
  const cleanPhone = selectedBranch?.phone ? selectedBranch.phone.replace(/\D/g, '') : '';

  const cardsData = [
    {
      id: 1,
      title: "Punto Seguro",
      description: "UN PUNTO SEGURO Y PROFESIONAL DONDE SUS CLIENTES PUEDAN RECOGER SUS PEDIDOS DE MANERA CÓMODA Y EFICIENTE",
      icon: "/Pagina_Punto_4.png",
      fallbackIcon: "/Pagina_Punto_4.png",
      delay: "0s"
    },
    {
      id: 2,
      title: "Límite de Peso",
      description: "EL PESO DE LOS PAQUETES NO DEBE SUPERAR LOS 10 KG. ESTE SERVICIO ESTÁ DISEÑADO PARA PRODUCTOS DE VOLUMEN BAJO Y MEDIO (ROPA, CALZADO, ACCESORIOS, COSMÉTICOS, TECNOLOGÍA, ETC.)",
      icon: "/Pagina_Punto_3.png",
      fallbackIcon: "/Pagina_Punto_3.png",
      delay: "0.2s"
    },
    {
      id: 3,
      title: "Embalaje y Guía",
      description: "TODO PRODUCTO DEBE VENIR PERFECTAMENTE CERRADO, SELLADO POR PARTE DEL EMPRENDEDEDOR. MÁS ENVÍOS SE ENCARGA DE GENERAR GUÍA",
      icon: "/Pagina_Punto_6.png",
      fallbackIcon: "/Pagina_Punto_6.png",
      delay: "0.4s"
    }
  ];

  const plansData = [
    {
      name: "Plan Emprendedor",
      desc: "Ideal para negocios y tiendas virtuales que están comenzando.",
      range: "1 a 50 pqts / mes",
      price: "$10",
      features: [
        "Sin contratos forzosos",
        "Esquema de pago flexible",
        "Ideal para ventas por redes sociales"
      ]
    },
    {
      name: "Plan Negocio",
      desc: "Para emprendedores con un flujo constante y activo de pedidos.",
      range: "51 a 150 pqts / mes",
      price: "$8",
      features: [
        "Menor costo por volumen",
        "Mayor margen de rentabilidad",
        "Ideal para e-commerce en pleno crecimiento"
      ]
    },
    {
      name: "Plan Empresarial",
      desc: "Para marcas y negocios consolidados con alto volumen de venta.",
      range: "151+ pqts / mes",
      price: "$6",
      features: [
        "Tarifa preferencial ultra baja",
        "Atención y soporte prioritaria",
        "Solución logística escalable"
      ]
    }
  ];

  return (
    <PageTransition>
      <div className="min-h-screen pt-24 pb-20 px-4 lg:px-6 relative overflow-hidden flex flex-col items-center justify-center">
        
        {/* Estilos CSS locales para efectos 3D, flotación, sombras y degradados animados */}
        <style dangerouslySetInnerHTML={{__html: `
          @keyframes float-left {
            0% { transform: translateY(0px) rotate(-6deg); }
            50% { transform: translateY(-12px) rotate(-5deg); }
            100% { transform: translateY(0px) rotate(-6deg); }
          }
          @keyframes float-right {
            0% { transform: translateY(0px) rotate(6deg); }
            50% { transform: translateY(-15px) rotate(7deg); }
            100% { transform: translateY(0px) rotate(6deg); }
          }
          @keyframes float-icon {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-12px); }
            100% { transform: translateY(0px); }
          }
          @keyframes shadow-scale {
            0%, 100% { transform: scale(1); opacity: 0.6; }
            50% { transform: scale(0.65); opacity: 0.3; }
          }
          @keyframes textShimmer {
            0% { background-position: 0% center; }
            100% { background-position: 200% center; }
          }
          @keyframes glow-pulse {
            0%, 100% { opacity: 0.25; transform: scale(1) translate(-50%, -50%); }
            50% { opacity: 0.4; transform: scale(1.15) translate(-50%, -50%); }
          }
          @keyframes gradientBG {
            0% { background-position: 50% 0%; }
            50% { background-position: 50% 100%; }
            100% { background-position: 50% 0%; }
          }
          @keyframes drawPath {
            to { stroke-dashoffset: 0; }
          }
          @keyframes flowLaser {
            0% { stroke-dashoffset: 1200; }
            100% { stroke-dashoffset: 0; }
          }
          @keyframes flowLaserMobile {
            0% { stroke-dashoffset: 500; }
            100% { stroke-dashoffset: 0; }
          }
          .animate-float-left {
            animation: float-left 4s ease-in-out infinite;
          }
          .animate-float-right {
            animation: float-right 4.5s ease-in-out infinite;
            animation-delay: 0.5s;
          }
          .animate-float-icon {
            animation: float-icon 4s ease-in-out infinite;
          }
          .animate-shadow-scale {
            animation: shadow-scale 4s ease-in-out infinite;
          }
          .animate-glow-pulse {
            animation: glow-pulse 6s ease-in-out infinite;
            transform-origin: top left;
          }
          .animate-draw-path {
            animation: drawPath 3.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
            animation-delay: 0.2s;
          }
          .animate-flow-laser {
            animation: flowLaser 5s linear infinite;
            animation-delay: 3.5s;
          }
          .animate-flow-laser-mobile {
            animation: flowLaserMobile 4s linear infinite;
            animation-delay: 3.5s;
          }
          
          /* Garantizar que el degradado shimmer de color rojo corporativo sea prioritario y se mantenga en versión oscura */
          .shimmer-text {
            background: linear-gradient(
              to right, 
              #CE010B 0%, 
              #FF555C 25%, 
              #CE010B 50%, 
              #FF555C 75%, 
              #CE010B 100%
            ) !important;
            background-size: 200% auto !important;
            color: transparent !important;
            -webkit-background-clip: text !important;
            background-clip: text !important;
            animation: textShimmer 3s linear infinite !important;
            filter: drop-shadow(0 2px 10px rgba(206, 1, 11, 0.55)) !important;
          }
          
          /* Degradado animado vertical de arriba a abajo C50000 */
          .hover-card-anim:hover, .hover-card-anim.is-opened {
            background: linear-gradient(to bottom, #C50000, #950000, #C50000);
            background-size: 100% 300%;
            animation: gradientBG 5s ease infinite;
          }
          .dark .hover-card-anim:hover, .dark .hover-card-anim.is-opened {
            background: linear-gradient(to bottom, #C50000, #750000, #C50000);
            background-size: 100% 300%;
            animation: gradientBG 5s ease infinite;
          }
          .scrollbar-none::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-none {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}} />

        <div className="max-w-[1550px] w-full mx-auto relative z-10 flex flex-col items-center justify-center">
          
          {/* TÍTULO PRINCIPAL INTEGRADO (FORMATO TÉRMINOS) */}
          <header className="z-35 w-full max-w-5xl mx-auto flex flex-col items-center text-center px-6">
            <div className="flex items-center justify-center gap-3">
              <Package className="w-8 h-8 md:w-10 md:h-10 text-white" />
              <h1 
                className="text-white text-3xl md:text-5xl font-black uppercase tracking-tight leading-none"
                style={{ fontFamily: "'Secular One', sans-serif" }}
              >
                Servicio Punto de Entrega
              </h1>
            </div>
            <div className="h-[1px] bg-white/20 w-full max-w-5xl my-4"></div>
          </header>

          {/* CONTENEDOR DE IMAGEN Y TEXTOS FLOTANTES */}
          <div className="relative w-full flex flex-col lg:flex-row items-center justify-center min-h-[460px] lg:min-h-[560px] mt-2 gap-8 lg:gap-0">
            
            {/* Resplandor radial detrás de la chica */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] lg:w-[700px] lg:h-[700px] bg-gradient-to-br from-[#CE010B] to-transparent rounded-full blur-[110px] pointer-events-none z-0 animate-glow-pulse" />

            {/* Texto Flotante Izquierdo (Desplazado más a la izquierda y con z-30 para estar al frente) */}
            <div className="lg:absolute lg:left-[12%] lg:top-[20%] z-30 text-center lg:text-left animate-float-left">
              <h2 
                className="text-white text-6xl lg:text-[76px] font-black uppercase tracking-tighter leading-[0.9] drop-shadow-[0_10px_25px_rgba(0,0,0,0.9)]"
                style={{ fontFamily: "'Secular One', sans-serif" }}
              >
                Más que un<br />paquete,
              </h2>
            </div>

            {/* Imagen Central (La chica con los paquetes, desplazada ligeramente a la izquierda y z-20) */}
            <div className="w-[115%] max-w-none lg:w-full lg:max-w-[1300px] lg:-translate-x-10 z-20 transition-all duration-700 hover:scale-[1.01] drop-shadow-[0_30px_60px_rgba(0,0,0,0.55)]">
              <img
                src="/Punto_Entrega_1.png"
                alt="Punto de entrega"
                className="w-full h-auto object-contain mx-auto"
                onError={(e) => {
                  e.target.src = "/Pagina_Punto_1.png";
                }}
              />
            </div>

            {/* Texto Flotante Derecho (Desplazado más a la derecha y con z-30 para estar al frente si se cruza) */}
            <div className="lg:absolute lg:right-[8%] lg:top-[23%] z-30 text-center lg:text-right animate-float-right">
              <h2 
                className="shimmer-text text-6xl lg:text-[76px] font-black uppercase tracking-tighter leading-[0.9]"
                style={{ fontFamily: "'Secular One', sans-serif" }}
              >
                Es tu marca
              </h2>
            </div>

          </div>

          {/* TÍTULO SECCIÓN DE SERVICIOS / PAQUETES */}
          <div className="text-center space-y-3 mt-16 mb-10 w-full max-w-[1200px] px-4">
            <span className="bg-[#CE010B] text-white font-black text-[18px] lg:text-[11px] uppercase tracking-widest px-7 py-2.5 lg:px-5 lg:py-1.5 rounded-full border border-white/20 shadow-lg inline-block">
              Nuestros Servicios
            </span>
            <h3 
              className="text-white text-[45px] lg:text-5xl font-black uppercase tracking-tight leading-none"
              style={{ fontFamily: "'Secular One', sans-serif" }}
            >
              Todo lo que necesitas saber
            </h3>
            <p className="text-neutral-400 max-w-xl mx-auto text-[21px] lg:text-sm font-semibold uppercase tracking-wider leading-relaxed">
              Haz clic en cada caja para abrir el paquete e interactuar con sus detalles.
            </p>
          </div>

          {/* SECCIÓN DE LOS PAQUETES ABRIR/DESLIZAR */}
          <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-8 pt-2 px-4">
            {cardsData.map((card) => {
              const isOpen = openedCards[card.id];
              return (
                <div 
                  key={card.id}
                  onClick={() => toggleCard(card.id)}
                  className={`group relative w-full max-w-[500px] min-h-[220px] h-auto lg:max-w-[260px] lg:h-[410px] mx-auto rounded-[2.5rem] border border-transparent bg-transparent shadow-none overflow-hidden flex flex-row lg:flex-col items-center justify-center p-5 cursor-pointer select-none transition-all duration-500 hover-card-anim
                    hover:border-[#CE010B]/30 dark:hover:border-[#CE010B]/50 hover:shadow-[0_20px_45px_rgba(206,1,11,0.25)] hover:scale-[1.02]
                    ${isOpen ? 'is-opened ring-2 ring-black/40 border-[#CE010B]/30 dark:border-[#CE010B]/50 shadow-[0_20px_45px_rgba(0,0,0,0.35)] scale-[1.02]' : ''}`}
                >
                  
                  {/* VISTA A: PAQUETE CERRADO */}
                  <div className={`absolute inset-0 flex flex-row lg:flex-col items-center justify-center p-5 gap-6 lg:gap-0 transition-all duration-500 ease-in-out pointer-events-none
                    ${isOpen 
                      ? 'translate-y-24 lg:translate-y-24 opacity-0 scale-75' 
                      : 'group-hover:translate-y-20 group-hover:opacity-0 group-hover:scale-75'
                    }`}
                  >
                    <div className="flex flex-col items-center justify-center shrink-0">
                      <img 
                        src="/Pagina_Punto_2.png" 
                        alt="Paquete cerrado" 
                        className="h-24 lg:h-38 w-auto object-contain z-10 animate-float-icon" 
                        style={{ animationDelay: card.delay }}
                      />
                      <div className="w-16 lg:w-22 h-2.5 lg:h-3 rounded-full bg-black/55 dark:bg-black/80 blur-[4px] lg:blur-[6px] animate-shadow-scale z-0 mt-0.5 lg:mt-1" style={{ animationDelay: card.delay }} />
                    </div>
                    <span className="text-white font-black uppercase tracking-widest text-[22px] lg:text-[13px] mt-0 lg:mt-4 text-left lg:text-center leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
                      {card.title}
                    </span>
                  </div>

                  {/* VISTA B: CONTENIDO REVELADO */}
                  <div className={`w-full h-full flex flex-row lg:flex-col items-center justify-between p-4 lg:p-2 gap-6 lg:gap-0 transition-all duration-500 ease-out z-10
                    ${isOpen 
                      ? 'opacity-100 translate-y-0 scale-100' 
                      : 'opacity-0 translate-y-20 scale-95 group-hover:opacity-100 group-hover:translate-y-0 group-hover:scale-100'
                    }`}
                  >
                    {/* Icono del Contenido 3D Flotando */}
                    <div className="relative flex flex-col items-center justify-center shrink-0 w-24 lg:w-full h-auto lg:h-40">
                      <div className={`relative flex flex-col items-center justify-center w-full ${card.id === 2 ? 'lg:-translate-x-5' : ''}`}>
                        <img 
                          src={card.icon} 
                          alt={card.title} 
                          className="h-22 lg:h-32 w-auto object-contain z-10 animate-float-icon"
                          style={{ animationDelay: card.delay }}
                          onError={(e) => {
                            e.target.src = card.fallbackIcon;
                          }}
                        />
                        <div className="absolute bottom-1 w-16 lg:w-24 h-2.5 lg:h-3.5 rounded-full bg-black/55 dark:bg-black/80 blur-[4px] lg:blur-[6px] animate-shadow-scale z-0 pointer-events-none" style={{ animationDelay: card.delay }} />
                      </div>
                    </div>

                    {/* Información del Servicio */}
                    <div className="text-left lg:text-center space-y-2 flex-grow flex flex-col justify-center">
                      <span className="text-white font-black uppercase tracking-wider text-[28px] lg:text-[15px] block underline decoration-2 underline-offset-4">
                        {card.title}
                      </span>
                      <p className="text-white font-extrabold uppercase tracking-tight text-[20px] lg:text-[11.5px] leading-relaxed">
                        {card.description}
                      </p>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>

          {/* TÍTULO SECCIÓN DE FLUJO / ¿CÓMO FUNCIONA? */}
          <div className="text-center space-y-3 mt-28 mb-14 w-full max-w-[1200px] flex flex-col items-center px-4">
            <span className="bg-[#CE010B] text-white font-black text-[18px] lg:text-[11px] uppercase tracking-widest px-7 py-2.5 lg:px-5 lg:py-1.5 rounded-full border border-white/20 shadow-lg inline-block">
              El Proceso
            </span>
            <div className="flex items-center justify-center gap-3">
              <div className="w-[5px] h-10 bg-[#CE010B] rounded-full" />
              <h3 
                className="text-white text-[45px] lg:text-5xl font-black uppercase tracking-tight leading-none"
                style={{ fontFamily: "'Secular One', sans-serif" }}
              >
                ¿CÓMO FUNCIONA?
              </h3>
            </div>
            <p className="text-neutral-400 max-w-xl mx-auto text-[21px] lg:text-sm font-semibold uppercase tracking-wider leading-relaxed">
              Sigue el camino trazado interactivo para ver el flujo logístico paso a paso.
            </p>
          </div>

          {/* SECCIÓN DE FLUJO Y CAMINO INTERACTIVO */}
          <div 
            ref={pathSectionRef}
            className="w-full max-w-[1300px] py-2 relative flex flex-col items-center justify-center"
          >
            
            {/* Camino SVG Interactivo Base */}
            <svg 
              className="absolute inset-0 w-full h-full pointer-events-none hidden lg:block" 
              viewBox="0 0 1280 400" 
              preserveAspectRatio="none"
            >
              <path 
                d="M 160 260 L 480 140 L 800 260 L 1120 140" 
                fill="none" 
                stroke="#CE010B" 
                strokeWidth="3.5" 
                strokeLinecap="round"
                strokeDasharray="1200" 
                strokeDashoffset="1200"
                className={`opacity-50 transition-all duration-300 ${drawPath ? 'animate-draw-path' : ''}`}
              />
              <path 
                d="M 160 260 L 480 140 L 800 260 L 1120 140" 
                fill="none" 
                stroke="#FF8B8B" 
                strokeWidth="4" 
                strokeLinecap="round"
                strokeDasharray="80, 250"
                strokeDashoffset="1200"
                className={`filter drop-shadow-[0_0_8px_rgba(206,1,11,0.8)] opacity-0 transition-opacity duration-500 ${drawPath ? 'opacity-100 animate-flow-laser' : ''}`}
              />
            </svg>

            {/* Camino SVG Móvil */}
            <svg 
              className="absolute inset-0 w-full h-full pointer-events-none lg:hidden" 
              viewBox="0 0 100 1000" 
              preserveAspectRatio="none"
            >
              <path 
                d="M 38 80 L 62 320 L 38 580 L 62 830" 
                fill="none" 
                stroke="#CE010B" 
                strokeWidth="1.5" 
                strokeLinecap="round"
                strokeDasharray="1000" 
                strokeDashoffset="1000"
                className={`opacity-50 transition-all duration-300 ${drawPath ? 'animate-draw-path' : ''}`}
              />
              <path 
                d="M 38 80 L 62 320 L 38 580 L 62 830" 
                fill="none" 
                stroke="#FF8B8B" 
                strokeWidth="2" 
                strokeLinecap="round"
                strokeDasharray="40, 150"
                strokeDashoffset="1000"
                className={`filter drop-shadow-[0_0_8px_rgba(206,1,11,0.8)] opacity-0 transition-opacity duration-500 ${drawPath ? 'opacity-100 animate-flow-laser-mobile' : ''}`}
              />
            </svg>

            {/* Cuadrícula de Pasos */}
            <div className="flex flex-col lg:grid lg:grid-cols-4 gap-4 lg:gap-6 w-full items-stretch lg:items-center relative z-10 min-h-[400px] px-16 lg:px-0">
              
              {/* PASO 1 (COL 1): TEXTO ARRIBA, ÍCONO ABAJO */}
              <div className="flex flex-col items-center justify-between h-[390px] lg:h-[370px] relative group/step w-full max-w-[280px] sm:max-w-[340px] lg:max-w-none self-start lg:self-auto">
                <div className="relative bg-gradient-to-b from-white/80 to-[#DCDCDC]/90 dark:from-tbt-red-top dark:to-tbt-red-bottom border border-black/10 dark:border-white/10 rounded-3xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.1)] hover:shadow-[0_0_20px_rgba(206,1,11,0.45)] hover:border-[#CE010B]/40 hover:scale-[1.03] transition-all duration-500 backdrop-blur-md w-full max-w-[340px] lg:max-w-[280px] min-h-[180px] lg:min-h-[160px] flex flex-col justify-center items-center">
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover/step:opacity-100 transition-opacity duration-700 pointer-events-none rounded-3xl" />
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-12 h-12 lg:w-9 lg:h-9 rounded-full bg-[#CE010B] dark:bg-white text-white dark:text-black font-black flex items-center justify-center border-[2.5px] border-black/20 dark:border-neutral-900 shadow-lg z-20 group-hover/step:rotate-[360deg] transition-transform duration-700 text-xl lg:text-sm">
                    1
                  </div>
                  <p className="text-[#CE010B] dark:text-[#FF4D4D] font-black uppercase text-center text-[21px] lg:text-[13px] leading-relaxed mt-4">
                    ENTREGUE LOS PEDIDOS ORGANIZADOS EN LA SUCURSAL SELECCIONADA DE MÁS ENVÍOS.
                  </p>
                  <div className="mt-3 text-[#CE010B]/60 dark:text-white/50">
                    <Truck className="w-6 h-6 lg:w-[18px] lg:h-[18px]" />
                  </div>
                </div>
                <div className="relative h-44 lg:h-36 flex items-center justify-center mt-4 transition-transform duration-500 group-hover/step:scale-110">
                  <img 
                    src="/Pagina_Punto_6.png" 
                    alt="Paso 1" 
                    className="h-40 lg:h-32 w-auto object-contain z-10 animate-float-icon" 
                  />
                  <div className="absolute bottom-1 w-24 lg:w-20 h-2.5 rounded-full bg-black/40 blur-[4px] animate-shadow-scale" />
                </div>
              </div>

              {/* PASO 2 (COL 2): ÍCONO ARRIBA, TEXTO ABAJO */}
              <div className="flex flex-col items-center justify-between h-[390px] lg:h-[370px] relative group/step w-full max-w-[280px] sm:max-w-[340px] lg:max-w-none self-end lg:self-auto">
                <div className="relative h-44 lg:h-36 flex items-center justify-center mt-4 lg:mt-0 lg:mb-4 transition-transform duration-500 group-hover/step:scale-110 order-2 lg:order-1">
                  <img 
                    src="/Pagina_Punto_7.png" 
                    alt="Paso 2" 
                    className="h-40 lg:h-32 w-auto object-contain z-10 animate-float-icon" 
                    style={{ animationDelay: "0.2s" }}
                  />
                  <div className="absolute bottom-1 w-24 lg:w-20 h-2.5 rounded-full bg-black/40 blur-[4px] animate-shadow-scale" style={{ animationDelay: "0.2s" }} />
                </div>
                <div className="relative bg-gradient-to-b from-white/80 to-[#DCDCDC]/90 dark:from-tbt-red-top dark:to-tbt-red-bottom border border-black/10 dark:border-white/10 rounded-3xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.1)] hover:shadow-[0_0_20px_rgba(206,1,11,0.45)] hover:border-[#CE010B]/40 hover:scale-[1.03] transition-all duration-500 backdrop-blur-md w-full max-w-[340px] lg:max-w-[280px] min-h-[180px] lg:min-h-[160px] flex flex-col justify-center items-center order-1 lg:order-2">
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover/step:opacity-100 transition-opacity duration-700 pointer-events-none rounded-3xl" />
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-12 h-12 lg:w-9 lg:h-9 rounded-full bg-[#CE010B] dark:bg-white text-white dark:text-black font-black flex items-center justify-center border-[2.5px] border-black/20 dark:border-neutral-900 shadow-lg z-20 group-hover/step:rotate-[360deg] transition-transform duration-700 text-xl lg:text-sm">
                    2
                  </div>
                  <p className="text-[#CE010B] dark:text-[#FF4D4D] font-black uppercase text-center text-[21px] lg:text-[13px] leading-relaxed mt-4">
                    SE REALIZA EL REGISTRO FORMAL DE LA MERCANCÍA RECIBIDA EN NUESTRO SISTEMA DE CONTROL.
                  </p>
                  <div className="mt-3 text-[#CE010B]/60 dark:text-white/50">
                    <ClipboardCheck className="w-6 h-6 lg:w-[18px] lg:h-[18px]" />
                  </div>
                </div>
              </div>

              {/* PASO 3 (COL 3): TEXTO ARRIBA, ÍCONO ABAJO */}
              <div className="flex flex-col items-center justify-between h-[390px] lg:h-[370px] relative group/step w-full max-w-[280px] sm:max-w-[340px] lg:max-w-none self-start lg:self-auto">
                <div className="relative bg-gradient-to-b from-white/80 to-[#DCDCDC]/90 dark:from-tbt-red-top dark:to-tbt-red-bottom border border-black/10 dark:border-white/10 rounded-3xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.1)] hover:shadow-[0_0_20px_rgba(206,1,11,0.45)] hover:border-[#CE010B]/40 hover:scale-[1.03] transition-all duration-500 backdrop-blur-md w-full max-w-[340px] lg:max-w-[280px] min-h-[180px] lg:min-h-[160px] flex flex-col justify-center items-center">
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover/step:opacity-100 transition-opacity duration-700 pointer-events-none rounded-3xl" />
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-12 h-12 lg:w-9 lg:h-9 rounded-full bg-[#CE010B] dark:bg-white text-white dark:text-black font-black flex items-center justify-center border-[2.5px] border-black/20 dark:border-neutral-900 shadow-lg z-20 group-hover/step:rotate-[360deg] transition-transform duration-700 text-xl lg:text-sm">
                    3
                  </div>
                  <p className="text-[#CE010B] dark:text-[#FF4D4D] font-black uppercase text-center text-[21px] lg:text-[13px] leading-relaxed mt-4">
                    EL CLIENTE FINAL ACUDE DE FORMA SEGURA A LA SUCURSAL PARA RECOGER Y RETIRAR SU COMPRA.
                  </p>
                  <div className="mt-3 text-[#CE010B]/60 dark:text-white/50">
                    <UserCheck className="w-6 h-6 lg:w-[18px] lg:h-[18px]" />
                  </div>
                </div>
                <div className="relative h-44 lg:h-36 flex items-center justify-center mt-4 transition-transform duration-500 group-hover/step:scale-110">
                  <img 
                    src="/Pagina_Punto_8.png" 
                    alt="Paso 3" 
                    className="h-40 lg:h-32 w-auto object-contain z-10 animate-float-icon"
                    style={{ animationDelay: "0.4s" }}
                  />
                  <div className="absolute bottom-1 w-24 lg:w-20 h-2.5 rounded-full bg-black/40 blur-[4px] animate-shadow-scale" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>

              {/* PASO 4 (COL 4): ÍCONO ARRIBA, TEXTO ABAJO */}
              <div className="flex flex-col items-center justify-between h-[390px] lg:h-[370px] relative group/step w-full max-w-[280px] sm:max-w-[340px] lg:max-w-none self-end lg:self-auto">
                <div className="relative h-44 lg:h-36 flex items-center justify-center mt-4 lg:mt-0 lg:mb-4 transition-transform duration-500 group-hover/step:scale-110 order-2 lg:order-1">
                  <img 
                    src="/Pagina_Punto_5.png" 
                    alt="Paso 4" 
                    className="h-40 lg:h-32 w-auto object-contain z-10 animate-float-icon"
                    style={{ animationDelay: "0.6s" }}
                  />
                  <div className="absolute bottom-1 w-24 lg:w-20 h-2.5 rounded-full bg-black/40 blur-[4px] animate-shadow-scale" style={{ animationDelay: "0.6s" }} />
                </div>
                <div className="relative bg-gradient-to-b from-white/80 to-[#DCDCDC]/90 dark:from-tbt-red-top dark:to-tbt-red-bottom border border-black/10 dark:border-white/10 rounded-3xl p-6 shadow-[0_10px_30px_rgba(0,0,0,0.1)] hover:shadow-[0_0_20px_rgba(206,1,11,0.45)] hover:border-[#CE010B]/40 hover:scale-[1.03] transition-all duration-500 backdrop-blur-md w-full max-w-[340px] lg:max-w-[280px] min-h-[180px] lg:min-h-[160px] flex flex-col justify-center items-center order-1 lg:order-2">
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent opacity-0 group-hover/step:opacity-100 transition-opacity duration-700 pointer-events-none rounded-3xl" />
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-12 h-12 lg:w-9 lg:h-9 rounded-full bg-[#CE010B] dark:bg-white text-white dark:text-black font-black flex items-center justify-center border-[2.5px] border-black/20 dark:border-neutral-900 shadow-lg z-20 group-hover/step:rotate-[360deg] transition-transform duration-700 text-xl lg:text-sm">
                    4
                  </div>
                  <p className="text-[#CE010B] dark:text-[#FF4D4D] font-black uppercase text-center text-[21px] lg:text-[13px] leading-relaxed mt-4">
                    SE CONFIRMA LA ENTREGA DE MANERA INMEDIATA Y AUTOMATIZADA AL VENDEDOR.
                  </p>
                  <div className="mt-3 text-[#CE010B]/60 dark:text-white/50">
                    <BellRing className="w-6 h-6 lg:w-[18px] lg:h-[18px]" />
                  </div>
                </div>
              </div>

            </div>

          </div>

          {/* SECCIÓN DE PLANES Y TARIFAS */}
          <div className="w-full max-w-[1200px] mt-24 px-4 flex flex-col items-center">
            
            {/* Encabezado elegante */}
            <div className="text-center space-y-3 mb-16 flex flex-col items-center px-4">
              <span className="bg-[#CE010B] text-white font-black text-[18px] lg:text-[11px] uppercase tracking-widest px-7 py-2.5 lg:px-5 lg:py-1.5 rounded-full border border-white/20 shadow-lg inline-block">
                Nuestras Tarifas
              </span>
              <h3 
                className="text-white text-[45px] lg:text-5xl font-black uppercase tracking-tight leading-none"
                style={{ fontFamily: "'Secular One', sans-serif" }}
              >
                Planes que se adaptan a ti
              </h3>
              <p className="text-neutral-400 max-w-xl mx-auto text-[21px] lg:text-sm font-semibold uppercase tracking-wider leading-relaxed">
                Elige el volumen ideal para tu negocio y obtén la tarifa más conveniente por paquete.
              </p>
            </div>

            {/* Grid de las 3 Tarjetas de Tarifas (Solo Desktop) */}
            <div className="hidden lg:grid grid-cols-3 gap-8 w-full">
              {plansData.map((plan, index) => {
                return (
                  <div 
                    key={index}
                    className="relative rounded-[2.5rem] p-8 lg:p-10 flex flex-col justify-between items-center transition-all duration-500 backdrop-blur-md border bg-gradient-to-b from-white/80 to-[#DCDCDC]/90 dark:from-tbt-red-top dark:to-tbt-red-bottom border-black/10 dark:border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.1)] hover:scale-[1.03] hover:border-[#CE010B]/40 hover:shadow-[0_20px_45px_rgba(206,1,11,0.25)]"
                  >
                    
                    {/* Encabezado del Plan */}
                    <div className="text-center w-full">
                      <h4 className="text-2xl font-black uppercase tracking-tight mb-2 text-black dark:text-white transition-colors duration-300 group-hover:text-[#CE010B]">
                        {plan.name}
                      </h4>
                      <p className="text-neutral-500 dark:text-neutral-400 text-[13px] font-semibold tracking-tight min-h-[40px] px-2 leading-relaxed">
                        {plan.desc}
                      </p>
                    </div>

                    {/* Rango de envíos / Badge */}
                    <div className="my-6">
                      <span className="bg-[#CE010B]/10 dark:bg-[#FF3333]/15 text-[#CE010B] dark:text-[#FF4D4D] font-black text-[12.5px] uppercase tracking-wider px-5 py-2 rounded-full border border-[#CE010B]/20">
                        {plan.range}
                      </span>
                    </div>

                    {/* Tarifa / Precio */}
                    <div className="text-center flex flex-col items-center my-4">
                      <span className="text-6xl font-black text-black dark:text-white tracking-tighter">
                        {plan.price}
                      </span>
                      <span className="text-[10px] font-black uppercase tracking-widest text-neutral-400 dark:text-neutral-500 mt-1">
                        Por Paquete Entregado
                      </span>
                    </div>

                    {/* Separador */}
                    <div className="w-full h-[1px] bg-black/10 dark:bg-white/10 my-6" />

                    {/* Lista de características */}
                    <ul className="space-y-4 w-full text-left">
                      {plan.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-[#CE010B] dark:text-[#FF3333] shrink-0 mt-0.5" />
                          <span className="text-[13px] font-bold text-neutral-700 dark:text-neutral-300 leading-tight">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                  </div>
                );
              })}
            </div>

            {/* Carrusel de Tarifas en Móvil */}
            <div className="lg:hidden flex flex-col items-center w-full px-4 relative select-none">
              {/* Contenedor Scrollable */}
              <div 
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex overflow-x-auto snap-x snap-mandatory scrollbar-none gap-6 w-full py-6 px-[10vw] scroll-smooth cursor-grab active:cursor-grabbing"
              >
                {plansData.map((plan, index) => {
                  return (
                    <div 
                      key={index}
                      className="snap-center shrink-0 w-[80vw] sm:w-[78vw] max-w-[480px] relative rounded-[2.5rem] p-12 flex flex-col justify-between items-center transition-all duration-500 backdrop-blur-md border bg-gradient-to-b from-white/80 to-[#DCDCDC]/90 dark:from-tbt-red-top dark:to-tbt-red-bottom border-black/10 dark:border-white/10 shadow-[0_15px_35px_rgba(0,0,0,0.12)] min-h-[660px]"
                    >
                      {/* Encabezado del Plan */}
                      <div className="text-center w-full">
                        <h4 className="text-[42px] font-black uppercase tracking-tight mb-2 text-black dark:text-white transition-colors duration-300 leading-none">
                          {plan.name}
                        </h4>
                        <p className="text-neutral-500 dark:text-neutral-400 text-[24px] font-semibold tracking-tight min-h-[60px] px-2 leading-relaxed mt-3">
                          {plan.desc}
                        </p>
                      </div>

                      {/* Rango de envíos / Badge */}
                      <div className="my-6">
                        <span className="bg-[#CE010B]/10 dark:bg-[#FF3333]/15 text-[#CE010B] dark:text-[#FF4D4D] font-black text-[22px] uppercase tracking-wider px-8 py-3.5 rounded-full border border-[#CE010B]/20 inline-block">
                          {plan.range}
                        </span>
                      </div>

                      {/* Tarifa / Precio */}
                      <div className="text-center flex flex-col items-center my-4">
                        <span className="text-[110px] font-black text-black dark:text-white tracking-tighter leading-none">
                          {plan.price}
                        </span>
                        <span className="text-[18px] font-black uppercase tracking-widest text-neutral-400 dark:text-neutral-500 mt-2">
                          Por Paquete Entregado
                        </span>
                      </div>

                      {/* Separador */}
                      <div className="w-full h-[1px] bg-black/10 dark:bg-white/10 my-8" />

                      {/* Lista de características */}
                      <ul className="space-y-6 w-full text-left">
                        {plan.features.map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-start gap-4">
                            <CheckCircle2 className="w-9 h-9 text-[#CE010B] dark:text-[#FF3333] shrink-0 mt-0.5" />
                            <span className="text-[24px] font-bold text-neutral-700 dark:text-neutral-300 leading-snug">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>

                    </div>
                  );
                })}
              </div>

              {/* Indicadores de puntos (Dots) */}
              <div className="flex gap-4 mt-4">
                {plansData.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => scrollToPlan(idx)}
                    className={`w-4 h-4 rounded-full transition-all duration-300 cursor-pointer ${
                      idx === activePlanIndex 
                        ? 'bg-[#CE010B] scale-125 shadow-[0_0_10px_rgba(206,1,11,0.6)]' 
                        : 'bg-neutral-300 dark:bg-neutral-600 hover:bg-neutral-400'
                    }`}
                    aria-label={`Ir al plan ${idx + 1}`}
                  />
                ))}
              </div>
            </div>

          </div>

          {/* SECCIÓN INTERACTIVA DE CONTACTO Y MAPA SUCURSALES (NUEVA UBICACIÓN: DEBAJO DE TARIFAS DE PLANES) */}
          <div className="w-full max-w-[1200px] mt-24 px-4 grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* Columna Izquierda: Contacto por WhatsApp (Ocupa 5/12) */}
            <div className="lg:col-span-5 relative p-8 lg:p-10 rounded-[2.5rem] border bg-gradient-to-b from-white/80 to-[#DCDCDC]/90 dark:from-tbt-red-top dark:to-tbt-red-bottom border-black/10 dark:border-white/10 shadow-[0_15px_35px_rgba(0,0,0,0.12)] hover:shadow-[0_20px_45px_rgba(206,1,11,0.25)] transition-all duration-500 backdrop-blur-md flex flex-col justify-between text-center lg:text-left gap-6 w-full max-w-[480px] lg:max-w-none mx-auto">
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent rounded-[2.5rem] pointer-events-none" />
              
              <div className="space-y-1 relative z-10">
                <span className="text-[15px] lg:text-[10px] font-black uppercase tracking-widest text-[#CE010B] dark:text-red-200/80 block">¿Tienes Preguntas?</span>
                <h4 className="text-[36px] lg:text-2xl font-black uppercase text-black dark:text-white tracking-tight leading-none lg:leading-tight">Aclaramos tus Dudas</h4>
                <p className="text-neutral-500 dark:text-neutral-300 text-[18px] lg:text-xs font-semibold uppercase tracking-wider pt-2 leading-relaxed">
                  ¿Quieres saber más del servicio de entrega? Selecciona tu sucursal más cercana y ponte en contacto directo por WhatsApp.
                </p>
              </div>

              {branches.length > 0 && (
                <div className="w-full text-left relative z-10">
                  <label className="text-[15px] lg:text-[10px] font-black uppercase tracking-widest text-neutral-500 dark:text-neutral-300 block mb-2 font-black">
                    Selecciona tu Sucursal:
                  </label>
                  <select 
                    className="w-full bg-white dark:bg-black/35 text-black dark:text-white border border-black/15 dark:border-white/15 rounded-2xl px-4 py-4.5 lg:py-3.5 text-[21px] lg:text-sm font-bold focus:outline-none focus:ring-2 focus:ring-[#CE010B] cursor-pointer uppercase tracking-wider"
                    value={selectedBranchId}
                    onChange={(e) => setSelectedBranchId(e.target.value)}
                  >
                    {branches.map(b => (
                      <option key={b.id} value={b.id} className="text-black">
                        {b.city || b.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {cleanPhone ? (
                <a
                  href={`https://wa.me/52${cleanPhone}?text=${encodeURIComponent("Hola, me gustaría aclarar algunas dudas sobre el servicio de Punto de Entrega.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-5 lg:py-4 bg-[#25D366] hover:bg-green-600 text-white font-black text-[21px] lg:text-sm uppercase tracking-widest rounded-2xl shadow-[0_10px_25px_rgba(37,211,102,0.3)] hover:shadow-[0_12px_30px_rgba(37,211,102,0.45)] transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-3 cursor-pointer relative z-10"
                >
                  <MessageCircle className="w-7 h-7 lg:w-5 lg:h-5 fill-white text-white" />
                  Contactar por WhatsApp
                </a>
              ) : (
                <div className="w-full py-4 px-4 bg-gray-100 dark:bg-black/30 border border-black/10 dark:border-white/10 rounded-2xl text-center text-sm lg:text-xs font-bold text-gray-500 dark:text-gray-400">
                  Estamos preparándonos para atenderte por este medio.
                </div>
              )}

              {selectedBranch && (
                <a
                  href={selectedBranch.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("Más Envíos " + (selectedBranch.name || selectedBranch.city) + " " + (selectedBranch.direccion || ''))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-4 lg:py-3.5 bg-[#CE010B] hover:bg-[#b00109] text-white font-black text-[18px] lg:text-xs uppercase tracking-widest rounded-2xl shadow-md transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2.5 cursor-pointer relative z-10 mt-1"
                >
                  <MapPin className="w-5 h-5 lg:w-4 lg:h-4 text-white" />
                  Abrir en Google Maps
                  <ExternalLink className="w-4 h-4 lg:w-3.5 lg:h-3.5 text-white/80" />
                </a>
              )}
            </div>

            {/* Columna Derecha: Mapa dinámico de Google Maps (Ocupa 7/12) */}
            <div className="lg:col-span-7 relative p-4 rounded-[2.5rem] border bg-gradient-to-b from-white/80 to-[#DCDCDC]/90 dark:from-tbt-red-top dark:to-tbt-red-bottom border-black/10 dark:border-white/10 shadow-[0_15px_35px_rgba(0,0,0,0.12)] transition-all duration-500 backdrop-blur-md min-h-[350px] flex items-stretch w-full max-w-[480px] lg:max-w-none mx-auto">
              <div className="w-full h-full rounded-[2rem] overflow-hidden border border-black/10 dark:border-white/15 bg-neutral-100 dark:bg-black/35 shadow-inner">
                {selectedBranch && selectedBranch.lat && selectedBranch.lng ? (
                  <iframe
                    title="Mapa Sucursal Seleccionada"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    src={`https://maps.google.com/maps?q=${selectedBranch.lat},${selectedBranch.lng}&output=embed`}
                    allowFullScreen
                    className="w-full h-full filter dark:invert-[0.9] dark:hue-rotate-180 border-0"
                  ></iframe>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-neutral-400 p-6 text-center gap-3">
                    <AlertCircle className="w-10 h-10 opacity-55 animate-pulse text-[#CE010B]" />
                    <span className="text-sm font-bold uppercase tracking-wider">Cargando ubicación de la sucursal...</span>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* SECCIÓN DE SEGURIDAD (DOS TARJETAS HORIZONTALES) */}
          <div className="w-full max-w-[900px] mt-28 px-4 flex flex-col items-center">
            
            {/* Encabezado elegante */}
            <div className="text-center space-y-3 mb-16 flex flex-col items-center px-4">
              <div className="flex items-center justify-center gap-2">
                <span className="bg-[#CE010B] text-white font-black text-[18px] lg:text-[11px] uppercase tracking-widest px-7 py-2.5 lg:px-5 lg:py-1.5 rounded-full border border-white/20 shadow-lg inline-flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  Seguridad Garantizada
                </span>
              </div>
              <h3 
                className="text-white text-[45px] lg:text-5xl font-black uppercase tracking-tight leading-none"
                style={{ fontFamily: "'Secular One', sans-serif" }}
              >
                Protocolo de Entrega Seguro
              </h3>
              <p className="text-neutral-400 max-w-xl mx-auto text-[21px] lg:text-sm font-semibold uppercase tracking-wider leading-relaxed">
                Garantizamos que tus envíos terminen únicamente en las manos correctas.
              </p>
            </div>

            {/* Listado de Tarjetas Horizontales */}
            <div className="flex flex-col gap-8 w-full">
              
              {/* Tarjeta Horizontal 1: Entrega al Titular */}
              <div className="relative flex flex-col lg:flex-row items-center lg:items-stretch gap-6 p-8 lg:p-10 rounded-[2.5rem] border bg-gradient-to-b from-white/80 to-[#DCDCDC]/90 dark:from-tbt-red-top dark:to-tbt-red-bottom border-black/10 dark:border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.1)] hover:scale-[1.01] hover:border-[#CE010B]/40 hover:shadow-[0_20px_45px_rgba(206,1,11,0.2)] transition-all duration-500 backdrop-blur-md w-full max-w-[480px] lg:max-w-none mx-auto">
                
                {/* Capa de reflejo */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent rounded-[2.5rem] pointer-events-none" />

                {/* Sección Izquierda: Icono */}
                <div className="flex items-center justify-center p-6 bg-[#CE010B]/10 dark:bg-white/10 rounded-[2rem] text-[#CE010B] dark:text-white shrink-0 w-28 h-28 lg:w-32 lg:h-auto">
                  <User className="w-16 h-16 lg:w-12 lg:h-12" />
                </div>

                {/* Sección Derecha: Información */}
                <div className="flex flex-col justify-between flex-grow text-center lg:text-left space-y-4">
                  <div>
                    <span className="text-[15px] lg:text-[10px] font-black uppercase tracking-widest text-neutral-400 dark:text-neutral-500 block mb-1">Opción 1</span>
                    <h4 className="text-[30px] lg:text-xl font-black uppercase text-black dark:text-white tracking-tight leading-none lg:leading-tight">Entrega al Titular</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Check className="w-7 h-7 lg:w-5 lg:h-5 text-[#CE010B] dark:text-[#FF3333] shrink-0 mt-0.5" />
                      <p className="text-[19.5px] lg:text-[13px] font-bold text-neutral-700 dark:text-neutral-300 leading-relaxed text-left uppercase">
                        EL COMPRADOR FINAL SE PRESENTA EN MOSTRADOR, MENCIONA SU NOMBRE Y MUESTRA UNA IDENTIFICACIÓN OFICIAL VIGENTE CON FOTOGRAFÍA (INE, PASAPORTE, LICENCIA).
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-7 h-7 lg:w-5 lg:h-5 text-[#CE010B] dark:text-[#FF3333] shrink-0 mt-0.5" />
                      <p className="text-[19.5px] lg:text-[13px] font-bold text-neutral-700 dark:text-neutral-300 leading-relaxed text-left uppercase">
                        EL OPERADOR VALIDA QUE EL NOMBRE COINCIDA CON LA GUÍA, EL CLIENTE FIRMA DE RECIBIDO Y SE LIBERA EL PAQUETE.
                      </p>
                    </div>
                  </div>
                </div>

              </div>

              {/* Tarjeta Horizontal 2: Tercero Autorizado */}
              <div className="relative flex flex-col lg:flex-row items-center lg:items-stretch gap-6 p-8 lg:p-10 rounded-[2.5rem] border bg-gradient-to-b from-white/80 to-[#DCDCDC]/90 dark:from-tbt-red-top dark:to-tbt-red-bottom border-black/10 dark:border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.1)] hover:scale-[1.01] hover:border-[#CE010B]/40 hover:shadow-[0_20px_45px_rgba(206,1,11,0.2)] transition-all duration-500 backdrop-blur-md w-full max-w-[480px] lg:max-w-none mx-auto">
                
                {/* Capa de reflejo */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent rounded-[2.5rem] pointer-events-none" />

                {/* Sección Izquierda: Icono */}
                <div className="flex items-center justify-center p-6 bg-[#CE010B]/10 dark:bg-white/10 rounded-[2rem] text-[#CE010B] dark:text-white shrink-0 w-28 h-28 lg:w-32 lg:h-auto">
                  <Users className="w-16 h-16 lg:w-12 lg:h-12" />
                </div>

                {/* Sección Derecha: Información */}
                <div className="flex flex-col justify-between flex-grow text-center lg:text-left space-y-4">
                  <div>
                    <span className="text-[15px] lg:text-[10px] font-black uppercase tracking-widest text-neutral-400 dark:text-neutral-500 block mb-1">Opción 2</span>
                    <h4 className="text-[30px] lg:text-xl font-black uppercase text-black dark:text-white tracking-tight leading-none lg:leading-tight">Tercero Autorizado</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Check className="w-7 h-7 lg:w-5 lg:h-5 text-[#CE010B] dark:text-[#FF3333] shrink-0 mt-0.5" />
                      <p className="text-[19.5px] lg:text-[13px] font-bold text-neutral-700 dark:text-neutral-300 leading-relaxed text-left uppercase">
                        SI EL COMPRADOR NO PUEDE IR PERSONALMENTE, LA PERSONA QUE RECOJA DEBE CONTAR CON UNA AUTORIZACIÓN EXPLÍCITA POR PARTE DEL CLIENTE DEL VENDEDOR.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-7 h-7 lg:w-5 lg:h-5 text-[#CE010B] dark:text-[#FF3333] shrink-0 mt-0.5" />
                      <p className="text-[19.5px] lg:text-[13px] font-bold text-neutral-700 dark:text-neutral-300 leading-relaxed text-left uppercase">
                        SE DEBE PRESENTAR LA IDENTIFICACIÓN ORIGINAL DEL TITULAR O MOSTRAR UN MENSAJE/CARTA DIGITAL DE AUTORIZACIÓN ACOMPAÑADA DE LA FOTO DE SU IDENTIFICACIÓN.
                      </p>
                    </div>
                  </div>
                </div>

              </div>

            </div>

          </div>

          {/* SECCIÓN DE LA POLÍTICA (ESFERAS FLOTANTES) */}
          <div className="w-full max-w-[1100px] mt-28 px-4 flex flex-col items-center">
            
            {/* Encabezado elegante */}
            <div className="text-center space-y-3 mb-20 flex flex-col items-center px-4">
              <div className="flex items-center justify-center gap-2">
                <span className="bg-[#CE010B] text-white font-black text-[18px] lg:text-[11px] uppercase tracking-widest px-7 py-2.5 lg:px-5 lg:py-1.5 rounded-full border border-white/20 shadow-lg inline-flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Aviso Importante
                </span>
              </div>
              <h3 
                className="text-white text-[45px] lg:text-5xl font-black uppercase tracking-tight leading-none"
                style={{ fontFamily: "'Secular One', sans-serif" }}
              >
                Política de Paquetes Olvidados
              </h3>
              <p className="text-neutral-400 max-w-xl mx-auto text-[21px] lg:text-sm font-semibold uppercase tracking-wider leading-relaxed">
                Para evitar la saturación de espacio con paquetes de menos de 10 kg que no son reclamados.
              </p>
            </div>

            {/* Listado de Bolas Flotantes */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 lg:gap-12 w-full max-w-[1000px] relative">
              
              {/* Esfera 1: Plazo de Recolección */}
              <div 
                className="group relative flex flex-col items-center justify-center rounded-full text-center p-8 mx-auto w-[90vw] h-[90vw] max-w-[480px] max-h-[480px] lg:w-[270px] lg:h-[270px] border bg-gradient-to-b from-white/80 to-[#DCDCDC]/90 dark:from-tbt-red-top dark:to-tbt-red-bottom border-black/10 dark:border-white/10 shadow-2xl hover:scale-105 hover:border-[#CE010B]/50 hover:shadow-[0_20px_45px_rgba(206,1,11,0.25)] transition-all duration-500 backdrop-blur-md cursor-pointer select-none animate-float-icon"
              >
                {/* Icono */}
                <div className="text-[#CE010B] dark:text-[#FF3333] mb-3 group-hover:scale-110 transition-transform duration-500">
                  <CalendarRange className="w-[72px] h-[72px] lg:w-9 lg:h-9" />
                </div>
                {/* Título */}
                <h4 className="text-[27px] lg:text-xs font-black uppercase text-black dark:text-white tracking-tight mb-3 px-2 leading-tight">
                  Plazo Límite (15 Días)
                </h4>
                {/* Descripción */}
                <p className="text-[22.5px] lg:text-[10px] font-extrabold text-neutral-700 dark:text-neutral-300 leading-normal px-2 max-w-[380px] lg:max-w-[210px] uppercase">
                  EL COMPRADOR TIENE UN PLAZO MÁXIMO DE 15 DÍAS NATURALES PARA RECOGER SU PAQUETE EN LA SUCURSAL.
                </p>
                {/* Sombra de esferas */}
                <div className="absolute -bottom-6 w-56 h-3 lg:w-32 lg:h-2.5 rounded-full bg-black/30 dark:bg-black/50 blur-[5px] animate-shadow-scale" />
              </div>

              {/* Esfera 2: Recordatorios Automáticos */}
              <div 
                className="group relative flex flex-col items-center justify-center rounded-full text-center p-8 mx-auto w-[90vw] h-[90vw] max-w-[480px] max-h-[480px] lg:w-[270px] lg:h-[270px] border bg-gradient-to-b from-white/80 to-[#DCDCDC]/90 dark:from-tbt-red-top dark:to-tbt-red-bottom border-black/10 dark:border-white/10 shadow-2xl hover:scale-105 hover:border-[#CE010B]/50 hover:shadow-[0_20px_45px_rgba(206,1,11,0.25)] transition-all duration-500 backdrop-blur-md cursor-pointer select-none animate-float-icon"
                style={{ animationDelay: "0.2s" }}
              >
                {/* Icono */}
                <div className="text-[#CE010B] dark:text-[#FF3333] mb-3 group-hover:scale-110 transition-transform duration-500">
                  <Send className="w-[72px] h-[72px] lg:w-9 lg:h-9" />
                </div>
                {/* Título */}
                <h4 className="text-[27px] lg:text-xs font-black uppercase text-black dark:text-white tracking-tight mb-3 px-2 leading-tight">
                  Recordatorios
                </h4>
                {/* Descripción */}
                <p className="text-[22.5px] lg:text-[10px] font-extrabold text-neutral-700 dark:text-neutral-300 leading-normal px-2 max-w-[380px] lg:max-w-[210px] uppercase">
                  SE ENVIARÁN RECORDATORIOS AUTOMÁTICOS AL COMPRADOR LOS DÍAS 3, 7 Y 12 DE LLEGADO SU PAQUETE.
                </p>
                {/* Sombra de esferas */}
                <div className="absolute -bottom-6 w-56 h-3 lg:w-32 lg:h-2.5 rounded-full bg-black/30 dark:bg-black/50 blur-[5px] animate-shadow-scale" style={{ animationDelay: "0.2s" }} />
              </div>

              {/* Esfera 3: Retorno a Origen */}
              <div 
                className="group relative flex flex-col items-center justify-center rounded-full text-center p-8 mx-auto w-[90vw] h-[90vw] max-w-[480px] max-h-[480px] lg:w-[270px] lg:h-[270px] border bg-gradient-to-b from-white/80 to-[#DCDCDC]/90 dark:from-tbt-red-top dark:to-tbt-red-bottom border-black/10 dark:border-white/10 shadow-2xl hover:scale-105 hover:border-[#CE010B]/50 hover:shadow-[0_20px_45px_rgba(206,1,11,0.25)] transition-all duration-500 backdrop-blur-md cursor-pointer select-none animate-float-icon"
                style={{ animationDelay: "0.4s" }}
              >
                {/* Icono */}
                <div className="text-[#CE010B] dark:text-[#FF3333] mb-3 group-hover:scale-110 transition-transform duration-500">
                  <RotateCcw className="w-[72px] h-[72px] lg:w-9 lg:h-9" />
                </div>
                {/* Título */}
                <h4 className="text-[27px] lg:text-xs font-black uppercase text-black dark:text-white tracking-tight mb-3 px-2 leading-tight">
                  Retorno (Día 15+)
                </h4>
                {/* Descripción */}
                <p className="text-[22.5px] lg:text-[10px] font-extrabold text-neutral-700 dark:text-neutral-300 leading-normal px-2 max-w-[380px] lg:max-w-[210px] uppercase">
                  SI EXPIRA EL PLAZO, SE NOTIFICARÁ AL EMPRENDEDOR PARA COORDINAR LA DEVOLUCIÓN A ORIGEN.
                </p>
                {/* Sombra de esferas */}
                <div className="absolute -bottom-6 w-56 h-3 lg:w-32 lg:h-2.5 rounded-full bg-black/30 dark:bg-black/50 blur-[5px] animate-shadow-scale" style={{ animationDelay: "0.4s" }} />
              </div>

            </div>

          </div>

        </div>
      </div>
    </PageTransition>
  );
}
